import * as XLSX from 'xlsx';

import type { PivotFileView, PivotedSheet } from './types';

const EXCEL_SHEET_NAME_MAX = 31;

function uniqueSheetName(used: Set<string>, desired: string): string {
  const base = desired.slice(0, EXCEL_SHEET_NAME_MAX);
  if (!used.has(base)) {
    used.add(base);
    return base;
  }
  for (let n = 2; n < 1000; n += 1) {
    const suffix = ` (${n})`;
    const name = `${base.slice(0, EXCEL_SHEET_NAME_MAX - suffix.length)}${suffix}`;
    if (!used.has(name)) {
      used.add(name);
      return name;
    }
  }
  return base;
}

function appendPivotedSheet({
  workbook,
  usedNames,
  sheet,
}: {
  workbook: XLSX.WorkBook;
  usedNames: Set<string>;
  sheet: PivotedSheet;
}): void {
  const name = uniqueSheetName(usedNames, sheet.sheetName);
  XLSX.utils.book_append_sheet(
    workbook,
    XLSX.utils.aoa_to_sheet([sheet.headers, ...sheet.rows]),
    name,
  );
}

export function writePivotedWorkbook(
  sheets: readonly PivotedSheet[],
): ArrayBuffer {
  const workbook = XLSX.utils.book_new();
  const usedNames = new Set<string>();
  for (const sheet of sheets) {
    appendPivotedSheet({ workbook, usedNames, sheet });
  }
  const written: unknown = XLSX.write(workbook, {
    type: 'array',
    bookType: 'xlsx',
  });
  if (written instanceof ArrayBuffer) return written;
  if (written instanceof Uint8Array) {
    const copy = new ArrayBuffer(written.byteLength);
    new Uint8Array(copy).set(written);
    return copy;
  }
  throw new Error('SheetJS did not return an ArrayBuffer');
}

export function pivotedSheetsFromViews(
  views: readonly PivotFileView[],
): PivotedSheet[] {
  return views.flatMap((view) =>
    view.status === 'pivoted' ? view.sheets : [],
  );
}
